import { useState } from "react";
import type { ScheduleEntry } from "@edu/shared";
import { ExternalCallForm, ExternalCallLink } from "./ExternalCall.tsx";

/**
 * Внешний созвон занятия: чужой Zoom или Телемост, названный доменом, и правка
 * его адреса.
 *
 * Своим файлом с 03.09.2026: карточка занятия упиралась в предел (раздел 9
 * CLAUDE.md), а это связный кусок со своим состоянием (раскрыта ли форма),
 * которое больше никому в карточке не нужно.
 *
 * ⚠️ **Ссылка стоит СРАЗУ НАД рядом действий, той же кнопкой `btn`, что и вход
 * в нашу комнату, и видна обеим сторонам.** Прятать её в меню, делать мельче
 * или уговаривать перейти к нам запрещено решением владельца 20.08.2026: так
 * теряется не комната, а расписание (раздел 7 `docs/switching-costs.md`). Своя
 * строка ей нужна не ради веса, а ради домена: чужой адрес обязан быть назван
 * словами.
 *
 * ⚠️ У отменённого и уехавшего занятия созвона нет вовсе: идти по ссылке
 * некуда, и предлагать её значит звать человека в пустую комнату. Признак
 * приезжает готовым (`dim`), потому что им же карточка гасит себя целиком, и
 * два способа ответить на один вопрос однажды разойдутся.
 */
export function EntryCall({
  entry,
  dim,
  busy,
  run,
}: {
  entry: ScheduleEntry;
  /** Занятие отменено или уехало: карточка приглушена, созвона нет. */
  dim: boolean;
  busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
}) {
  /** Форма ссылки на созвон раскрывается по нажатию, как и остальные формы. */
  const [callEditing, setCallEditing] = useState(false);

  return (
    <>
      {!dim && (entry.externalCallUrl !== null || entry.myRole === "teacher") && (
        <div className="wk__call">
          <ExternalCallLink url={entry.externalCallUrl} />
          {entry.myRole === "teacher" && !callEditing && (
            <button
              type="button"
              className="btn btn--quiet"
              disabled={busy}
              onClick={() => setCallEditing(true)}
            >
              {entry.externalCallUrl === null ? "Ссылка на созвон" : "Поменять"}
            </button>
          )}
        </div>
      )}
      {entry.myRole === "teacher" && callEditing && (
        <ExternalCallForm
          lessonId={entry.lessonId}
          url={entry.externalCallUrl}
          own={entry.externalCallOwn}
          seriesId={entry.seriesId}
          /*
            ⚠️ Вид занятия едет ПОЛЕМ, а не выводится формой из пустоты урока и
            серии: у занятия группы бывают и урок (сводная строка с разбором), и
            серия (осиротевший урок во втором потоке), а своей ссылки у него не
            бывает никогда. Ровно тем же полем и по той же причине рисуется
            фигура в `EntryKind.tsx`.

            ⚠️ Поля нет в ответе ученику вовсе, и читать его отсутствие как
            «занятие личное» нельзя. Здесь это безопасно: весь блок стоит под
            проверкой роли, то есть форму видит только преподаватель
          */
          group={entry.kind === "group"}
          /*
            ⚠️ Группа доезжает до формы ТОЛЬКО отсюда: карточка это
            единственное место, где её идентификатор вообще есть, и без него
            дверь из блока созвона вела в список серий, то есть на шаг дальше,
            чем нужна. Поле `group` приходит только преподавателю (у ученика его
            нет вовсе), а форму видит тоже только он
          */
          groupId={entry.group?.id ?? null}
          busy={busy}
          run={run}
          onDone={() => setCallEditing(false)}
        />
      )}
    </>
  );
}
